import * as React from "react"
import { CartSolutionOrder } from "../../../../utils/models"
import styled from "styled-components"
import H3 from "../../../atoms/typography/H3"
import Body from "../../../atoms/typography/Body"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import { getFormattedPrice } from "../../../../utils/cart-helpers"
import { getWarranty } from "../../../../hooks-saleor/getWarrantyQuery"
import { EditIcon } from "../../../atoms/icons/EditIcon"
import { CartStructure, Product, Variant } from "../../../interfaces/SaleorTypes"
import IconButton from "../../../atoms/buttons/IconButton"
import { MinusIcon } from "../../../atoms/icons/MinusIcon"

interface CartSolutionSectionProps {
  solutions: Array<CartSolutionOrder>
  editable?: boolean
  removeSolution?: (id: string) => void
  removeFromCart?: (products: CartStructure[]) => void
}

const TerminalImage = styled.img`
  width: 56px;
  height: 60px;
  object-fit: contain;
`

const FlexContainer = styled.div`
  display: flex;
`

const SpaceBetweenContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const SpaceAndAlignCenter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CartSolutionSection: React.FC<CartSolutionSectionProps> = ({
  solutions,
  editable,
  removeSolution,
  removeFromCart,
}) => {
  const { warranty } = getWarranty()

  const onClickEdit = (
    solutionid: string,
    terminal: Product,
    terminalAmount: number,
    warranties: number,
    goToEdit: boolean,
    modem?: Product,
    variant?: Variant,
  ) => {
    let pruductsToRemove: Array<CartStructure> = []
    const terminalToRemove: CartStructure = { product: terminal, amount: terminalAmount }
    const warrantsToRemove: CartStructure = { product: warranty, amount: warranties }

    if (variant !== undefined) {
      const dpProduct: Product = {
        id: variant.product!.id,
        name: variant.product!.name,
        metadata: [...variant.product!.metadata, { key: "dataPlanStartDate", value: "" }],
        media: variant.product!.media,
        description: variant.product!.description,
        variants: [variant],
        category: variant.product!.category,
      }
      const dp: CartStructure = { product: dpProduct, amount: terminalAmount }
      pruductsToRemove = [...pruductsToRemove, dp]
    }

    if (modem) {
      const modemToRemove: CartStructure = { product: modem, amount: terminalAmount }
      pruductsToRemove = [...pruductsToRemove, modemToRemove]
    }

    pruductsToRemove = [...pruductsToRemove, terminalToRemove, warrantsToRemove]

    if (removeFromCart && removeSolution) {
      removeFromCart(pruductsToRemove)
      removeSolution(solutionid)
    }
    const isKu = terminal.name.includes("Ku") ? true : false

    if (goToEdit) {
      if (isKu) {
        window.location.href = "/store/customize/ku"
      } else {
        window.location.href = "/store/customize/secure"
      }
    }
  }

  return (
    <div>
      {solutions.map((solution) => (
        <div key={solution.id}>
          <SpaceAndAlignCenter>
            <FlexContainer>
              <TerminalImage src={solution.terminal.media[0]?.url} />
              <div>
                <H3>{solution.terminal.name}</H3>
                <Body>{solution.modem?.name}</Body>
              </div>
            </FlexContainer>
            {editable && (
              <FlexContainer>
                <IconButton
                  icon={<EditIcon />}
                  onClick={() =>
                    onClickEdit(
                      solution.id,
                      solution.terminal,
                      solution.terminalAmount,
                      solution.extendedWarranty,
                      true,
                      solution.modem,
                      solution.dataPlan,
                    )
                  }
                />
                <Spacer spacing={1} />
                <IconButton
                  icon={<MinusIcon />}
                  onClick={() =>
                    onClickEdit(
                      solution.id,
                      solution.terminal,
                      solution.terminalAmount,
                      solution.extendedWarranty,
                      false,
                      solution.modem,
                      solution.dataPlan,
                    )
                  }
                />
              </FlexContainer>
            )}
          </SpaceAndAlignCenter>
          <HorizontalSpacer spacing={0.5} />
          <SpaceAndAlignCenter>
            <Body>Quantity:</Body>
            <Body>{solution.terminalAmount}</Body>
          </SpaceAndAlignCenter>
          <HorizontalSpacer spacing={0.5} />
          <SpaceAndAlignCenter>
            <Body>Price:</Body>
            <Body>
              {getFormattedPrice(
                (solution.terminal.variants[0]?.pricing.price.gross.amount ?? 0) * solution.terminalAmount,
                solution.terminal?.variants[0]?.pricing.price.currency,
              )}
            </Body>
          </SpaceAndAlignCenter>
          {solution.dataPlan && (
            <>
              <HorizontalSpacer spacing={0.5} />
              <SpaceBetweenContainer>
                <div>
                  <H3>Data plan {solution.dataPlan?.metadata[0]?.value}</H3>
                  <HorizontalSpacer spacing={0.5} />

                  <Body>{solution.dataPlan?.name} - 1 year validity</Body>
                </div>

                <Body>
                  {getFormattedPrice(
                    solution.dataPlan.pricing.price.gross.amount * solution.terminalAmount,
                    solution.terminal.variants[0]?.pricing.price.currency,
                  )}
                </Body>
              </SpaceBetweenContainer>
            </>
          )}
          {solution.extendedWarranty > 0 && (
            <div>
              <HorizontalSpacer spacing={0.5} />
              <SpaceBetweenContainer>
                <div>
                  <H3>Extended warranty</H3>
                  <Body>{solution.extendedWarranty} years</Body>
                </div>
                <Body>
                  {getFormattedPrice(
                    (warranty?.variants[0]?.pricing.price.gross.amount ?? 0) * solution.terminalAmount,
                    solution.terminal.variants[0]?.pricing.price.currency,
                  )}
                </Body>
              </SpaceBetweenContainer>
            </div>
          )}

          <HorizontalSpacer spacing={1.5} />
          <H3>Satcube accessories starter pack</H3>
          <HorizontalSpacer spacing={1.5} />
          <SpaceAndAlignCenter>
            <Body>Quantity:</Body>
            <Body>{solution.terminalAmount}</Body>
          </SpaceAndAlignCenter>
          <HorizontalSpacer spacing={0.5} />
          <SpaceAndAlignCenter>
            <Body>Price:</Body>
            <Body>Included</Body>
          </SpaceAndAlignCenter>

          <HorizontalSpacer spacing={2} />
        </div>
      ))}
    </div>
  )
}

export default CartSolutionSection
